<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <transition name="drawer-fade">
      <div v-show="modelValue" class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[100]" @click="close"></div>
    </transition>

    <!-- Drawer -->
    <transition name="drawer-slide">
      <div
        v-show="modelValue"
        class="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl z-[101] flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="p-5 border-b border-gray-200 dark:border-gray-800">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-bold tracking-tight text-gray-900 dark:text-white">{{ tt('Menu', 'Meni') }}</h3>
            <button
              @click="close"
              class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white transition-colors">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <!-- User Info (when cloud authenticated) -->
          <div v-if="isCloudAuthenticated && cloudUser" class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-full bg-primary-400 flex items-center justify-center overflow-hidden flex-shrink-0 text-gray-900">
              <img
                v-if="cloudUser.image"
                :src="cloudUser.image"
                :alt="cloudUser.name || tt('User', 'Uporabnik')"
                class="w-full h-full object-cover"
                @error="
                  (e: Event) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }
                " />
              <span v-else class="text-lg font-bold">{{ userInitials }}</span>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {{ cloudUser.name || cloudUser.email }}
              </p>
              <p v-if="cloudUser.name" class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ cloudUser.email }}
              </p>
            </div>
          </div>

          <!-- Not signed in state -->
          <div v-else class="text-center py-2">
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {{ tt('Sign in to access cloud features', 'Prijavite se za dostop do oblačnih funkcij') }}
            </p>
            <button
              @click="emit('signIn')"
              class="w-full py-2.5 bg-primary-400 hover:bg-primary-500 text-gray-900 rounded-full font-semibold text-sm transition-colors">
              {{ tt('Sign in to', 'Prijavite se v') }} {{ cloudName }}
            </button>
          </div>
        </div>

        <!-- Menu Items -->
        <nav class="flex-1 overflow-y-auto p-3 space-y-1">
          <!-- Settings -->
          <button
            @click="openSettings"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white transition-colors">
            <span class="material-symbols-outlined text-xl text-gray-500 dark:text-gray-400">settings</span>
            {{ tt('Settings', 'Nastavitve') }}
          </button>

          <!-- App Settings (sub-item) -->
          <button
            @click="openSettings"
            class="w-full flex items-center gap-3 px-4 py-3 pl-12 rounded-xl text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors">
            <span class="material-symbols-outlined text-lg">tune</span>
            {{ tt('App Settings', 'Nastavitve aplikacije') }}
          </button>

          <!-- Touch Mode -->
          <button
            @click="enterTouchMode"
            class="w-full flex items-center gap-3 px-4 py-3 pl-12 rounded-xl text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors">
            <span class="material-symbols-outlined text-lg">touch_app</span>
            {{ tt('Touch Mode', 'Način dotikalne plošče') }}
          </button>

          <div class="my-3 border-t border-gray-200 dark:border-gray-800"></div>

          <!-- Theme Toggle -->
          <button
            @click="cycleTheme"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white transition-colors">
            <span class="material-symbols-outlined text-xl text-gray-500 dark:text-gray-400">{{
              isDark ? 'dark_mode' : 'light_mode'
            }}</span>
            {{ isDark ? tt('Dark Mode', 'Temni način') : tt('Light Mode', 'Svetli način') }}
          </button>

          <!-- Account Settings (opens the web account area in the browser) -->
          <button
            @click="openAccountSettings"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white transition-colors">
            <span class="material-symbols-outlined text-xl text-gray-500 dark:text-gray-400">manage_accounts</span>
            <span class="flex-1 text-left">{{ tt('Account Settings', 'Nastavitve računa') }}</span>
            <span class="material-symbols-outlined text-base text-gray-400 dark:text-gray-500">open_in_new</span>
          </button>

          <div class="my-3 border-t border-gray-200 dark:border-gray-800"></div>

          <!-- Sign Out (when cloud authenticated) -->
          <button
            v-if="isCloudAuthenticated"
            @click="emit('signOut')"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 transition-colors">
            <span class="material-symbols-outlined text-xl">logout</span>
            {{ tt('Sign Out', 'Odjava') }}
          </button>
        </nav>

        <!-- Footer / About -->
        <div class="p-4 border-t border-gray-200 dark:border-gray-800">
          <h4 class="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 mb-1.5">
            {{ tt('About', 'O programu') }}
          </h4>
          <p class="text-sm font-medium text-gray-900 dark:text-white">{{ brand.platformName }} v{{ appVersion }}</p>
          <button
            @click="openWebsite"
            class="mt-1 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors">
            {{ siteHost }}
            <span class="material-symbols-outlined text-sm">open_in_new</span>
          </button>
          <p class="text-[11px] text-gray-400 dark:text-gray-600 mt-3">
            {{ brand.platformName }} &copy; {{ currentYear }}
          </p>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { brand, cloudName } from '../../config/brand';
import { useThemeStore } from '../../stores/theme';
import { openExternalUrl } from '../../services/externalLink';
import { tt } from '../../utils/consoleText';

interface CloudUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
}

const props = defineProps<{
  modelValue: boolean;
  cloudUser: CloudUser | null;
  isCloudAuthenticated: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  signIn: [];
  signOut: [];
  openSettings: [];
  enterTouchMode: [];
}>();

const themeStore = useThemeStore();

const currentYear = new Date().getFullYear();
const isDark = computed(() => themeStore.isDark);

/** Build-time app version (vite define) — shown in the About block. */
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
/** Public site host without the scheme, e.g. "torik.ai". */
const siteHost = brand.siteUrl.replace(/^https?:\/\//, '');

const userInitials = computed(() => {
  if (props.cloudUser?.name) {
    const parts = props.cloudUser.name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return props.cloudUser.name.substring(0, 2).toUpperCase();
  }
  if (props.cloudUser?.email) {
    return props.cloudUser.email.substring(0, 2).toUpperCase();
  }
  return '?';
});

function close() {
  emit('update:modelValue', false);
}

function cycleTheme() {
  themeStore.cycleTheme();
}

function openSettings() {
  emit('openSettings');
}

function enterTouchMode() {
  emit('enterTouchMode');
}

/** The account area lives on the web — open it in the user's own browser. */
function openAccountSettings() {
  close();
  void openExternalUrl(`${brand.siteUrl}/account`);
}

function openWebsite() {
  close();
  void openExternalUrl(brand.siteUrl);
}
</script>

<style scoped>
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.25s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
